import { isPlatformBrowser } from "@angular/common";
import { HttpClient, HttpParams, HttpBackend, HttpResponse } from "@angular/common/http";
import { Inject, Injectable, OnDestroy, OnInit, PLATFORM_ID, EventEmitter, Output } from "@angular/core";
import { Router } from "@angular/router";
import { BehaviorSubject, interval, Subscription, Observable, Subject } from "rxjs";
import { tap, map, mergeMap } from 'rxjs/operators';

import { Claim } from "../admin/models/claim.model";
import { NavigationService } from "../core/services/navigation.service";
import { NavigationRoot } from "../core/models/navigation-root.model";
import { LunaClaimTypes, LunaClaimValues, UserIdentity } from "../models/user-identity.model";
import { Widget, WidgetKind } from "../dashboard/models/dashboard.dto";
import { AgentOrg } from "../models/agentOrg.model";
import { LoaderService } from "../common/services/loader.service";
import { TimeoutConfig, TokenValidationExceptionMessages } from "./auth.config";

export class LoginTokenResponse {
    token: string;
    profile: Claim[];
    expiration: string;
    expDate: string;
    agentOrgs: AgentOrg[];
}

@Injectable({
    providedIn: 'root'
})
export class AuthService implements OnDestroy {

    tokenExceptions: TokenValidationExceptionMessages[] = [
        TokenValidationExceptionMessages.Expired,
        TokenValidationExceptionMessages.InvalidIP,
        TokenValidationExceptionMessages.NonExistent,
        TokenValidationExceptionMessages.Revoked
    ];

    protected loggedInUser: BehaviorSubject<UserIdentity> = new BehaviorSubject<UserIdentity>(new UserIdentity([], "", false)); //don't reference the BehaviorSubject directly, use the updateUser function instead as it ensures the organizationId is properly associated
    public loggedInUser$: Observable<UserIdentity> = this.loggedInUser.asObservable();

    private tokenSubject: BehaviorSubject<string> = new BehaviorSubject<string>("");
    public token$: Observable<string> = this.tokenSubject.asObservable();
    private expirationSubject: Subject<any> = new Subject<any>();
    public expDate$: Observable<any> = this.expirationSubject.asObservable();

    private selectedOrganization: BehaviorSubject<string> = new BehaviorSubject<string>("");
    public selectedOrganization$: Observable<string> = this.selectedOrganization.asObservable();

    private requireLogout: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public requireLogoutObs: Observable<boolean> = this.requireLogout.asObservable();
    public sessionRefresh: Subject<LoginTokenResponse> = new Subject<LoginTokenResponse>();
    public httpClient: HttpClient;
    public promptTimeout = new Subject<any>();
    private navigationMenu: BehaviorSubject<NavigationRoot> = new BehaviorSubject<NavigationRoot>({ panels: [] } as NavigationRoot);
    userNavigation: Observable<NavigationRoot> = this.navigationMenu.asObservable();

    private claimArr: Claim[] = [];
    private updateClaimsSubject = new Subject<Claim[]>();

    private cookiesAccepted: BehaviorSubject<Boolean> = new BehaviorSubject<Boolean>(false);
    public cookiesAccepted$: Observable<Boolean> = this.cookiesAccepted.asObservable();

    userClaims: Observable<Claim[]> = this.updateClaimsSubject.asObservable();

    private menuSubscription: Subscription;
    profileSubscription: Subscription;
    get apiToken(): string {
        let value = sessionStorage.getItem("api_token");
        if (value == null) value = sessionStorage.getItem("anonymous_api_token");
        return value;
    }

    set apiToken(value: string) {
        sessionStorage.removeItem("api_token");
        sessionStorage.setItem("api_token", value);
        this.tokenSubject.next(value);
    }

    get tokenExpirationDate(): any {
        return sessionStorage.getItem("expDate");
    }

    set tokenExpirationDate(expiration: any) {
        sessionStorage.setItem("expDate", expiration);
        this.expirationSubject.next(expiration);
    }

    get cookieAcceptance(): boolean {
        let value: unknown = sessionStorage.getItem("acceptCookies");
        if (value == null) value = this.getAcceptanceCookie();
        let result: boolean = value != null && (value == true || value == 'true');
        return result;
    }

    set cookieAcceptance(accept: boolean) {
        sessionStorage.setItem("acceptCookies", accept.toString());
        if (accept) {
            let expiration = new Date();
            expiration.setTime(expiration.getTime() + (30 * 24 * 60 * 60 * 1000))
            let expires = `expires=${expiration.toUTCString()}`;
            document.cookie = `RICS_COOKIE_ACCEPTANCE=${accept};expires=${expires}`;
        }
        this.cookiesAccepted.next(accept);
    }

    getAcceptanceCookie() {
		let name = "RICS_COOKIE_ACCEPTANCE";
        let ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) == ' ') {
                c = c.substring(1);
            }
            if (c.indexOf(name) == 0) {
                let parts = c.split('=')
                if (parts.length > 1) return parts[1];
                else return c.substring(name.length + 1, c.length);
            }
        }
        return "";
    }

    constructor(
        public readonly router: Router,
        public backend: HttpBackend, public loader: LoaderService, private readonly navigation: NavigationService
    ) {
        if (this.cookieAcceptance == null) this.cookieAcceptance = false;

        this.httpClient = new HttpClient(backend);
        var isLoggedIn = sessionStorage.getItem("api_token");
        if (isLoggedIn) {
            let str = sessionStorage.getItem("user");
            let userCurrentOrgId = sessionStorage.getItem("selectedOrg");
            let data = JSON.parse(str);
            this.loggedInUser.next(new UserIdentity(data, sessionStorage.getItem("userEmail"), true));
            if (userCurrentOrgId != undefined && userCurrentOrgId != "" && this.loggedInUser.value.organizationId != userCurrentOrgId)
                this.selectOrganization(userCurrentOrgId);
        }         
    }

    ngOnDestroy() {
        if (this.menuSubscription) this.menuSubscription.unsubscribe();
        if (this.profileSubscription) this.profileSubscription.unsubscribe();
    }

    public forceLogout(value: boolean): void {
        this.requireLogout.next(value);
    }  


    updateClaims(claims: Claim[]): void {
        this.updateClaimsSubject.next(claims);
    }

    getTimeoutConfig(): Observable<TimeoutConfig> {
        return this.httpClient.get<TimeoutConfig>("/api/auth/getTimeouts");
    }

    public selectOrganization(orgId: string): void {
        const currState = this.loggedInUser.getValue();
        currState.organizationId = orgId;
        this.updateUser(currState);
        sessionStorage.setItem("selectedOrg", orgId);
        this.selectedOrganization.next(orgId);
    }

    public setProfile(): Observable<UserIdentity> {
        if (this.menuSubscription == null) {
            this.menuSubscription = this.userClaims.pipe
                (mergeMap((claims: Claim[]) => {
                    let orgClaim = claims.find(c => c.type == 'org');
                    if (orgClaim != null) this.selectOrganization(orgClaim.value);
                    return this.generateNavigation(true);
                })).subscribe(
                    (data: NavigationRoot) => {
                        this.navigationMenu.next(data);
                    },
                    (error) => { console.error(error); }
                );
        }

        return this.httpClient.get<LoginTokenResponse>("/api/auth/public-profile", { observe: 'response' }).pipe(map((response: HttpResponse<LoginTokenResponse>) => {
            this.sessionRefresh.next(response.body);
            if (response.headers.get("location") != null) {
                var identity = new UserIdentity([], "", false);
                sessionStorage.setItem("user", JSON.stringify([]));
                sessionStorage.setItem("orgList", JSON.stringify([]));
                this.updateUser(identity);
                window.location.href = response.headers.get('location');
                return;
            } else {
                let tokenResponse: LoginTokenResponse = response.body;
                this.updateClaimsSubject.next(tokenResponse.profile);
                let authClaim = tokenResponse.profile.find(x => x.type.toLocaleLowerCase() == "auth");
                var canLoginClaim = tokenResponse.profile.find(x => x.type.toLocaleLowerCase() == "canlogin");
                var emailClaim = tokenResponse.profile.find(x => x.type.toLocaleLowerCase() == "email");
                if (authClaim && authClaim.value == "y" && emailClaim && canLoginClaim && canLoginClaim.value == "y" && tokenResponse.token) {
                    this.apiToken = tokenResponse.token;
                    if (sessionStorage.getItem("user")) {
                        sessionStorage.removeItem("user"); //reset
                    }
                    sessionStorage.setItem("user", JSON.stringify(tokenResponse.profile));
                    sessionStorage.setItem("userEmail", emailClaim.value);

                    this.tokenExpirationDate = (new Date(tokenResponse.expDate)).toISOString();
                    sessionStorage.setItem("orgList", JSON.stringify(tokenResponse.agentOrgs));
                    var identity = new UserIdentity(tokenResponse.profile, emailClaim.value, true);
                    this.updateUser(identity);
                    return identity;
                }
                //here's the old stuff
                sessionStorage.setItem("orgList", JSON.stringify([]));
                sessionStorage.setItem("anonymous_api_token", tokenResponse.token);
                this.tokenExpirationDate = (new Date(tokenResponse.expDate)).toISOString();
                var identity = new UserIdentity(tokenResponse.profile, '', false);
                this.updateUser(identity);
                return identity;
            }
           
        }));
    }

    public notFound(): void {
        this.router.navigate(["/home/login"], { queryParams: { notfound: true } });
    }

    public static buildWidgetKindClaimLookup(): { [kind: string]: string; } {
        let res: { [kind: string]: string } = {};
        for (let k in WidgetKind) {
            if (WidgetKind.hasOwnProperty(k)) {
                res[k] = LunaClaimValues[`View${k}Widget`];
            }
        }
        return res;
    }

    public static widgetKindClaimLookup: { [kind: string]: string; } = AuthService.buildWidgetKindClaimLookup();

    get isLoggedIn(): boolean {
        var token = sessionStorage.getItem("api_token");
        return token !== null && token !== undefined && token !== "";
    }

    get userEmail(): string {
        return this.loggedInUser.getValue().email;
    }

    get userLatestClaims(): Claim[] {
        return this.claimArr;
    }

    get User(): UserIdentity {
        return this.loggedInUser.getValue();
    }

    get authorizationHeader(): string {
        // the space after Bearer is intentional and required by http spec
        return `Bearer ${this.apiToken}`;
    }

    public generateNavigation(regenerate: boolean = false): Observable<NavigationRoot> {
        let obs$: Observable<NavigationRoot>;
        if (regenerate) {
            obs$ = this.navigation.getNavigation();
        } else if (this.navigationMenu.getValue().panels.length === 0) {
            obs$ = this.loggedInUser$.pipe(
                mergeMap((user: UserIdentity) => {
                    return this.navigation.getNavigation();
                })              
                )
        } else {
            obs$ = this.userNavigation;
        }

        return obs$;
    }


    public hasSystemPermission(permission: string) {
        return this.User.systemPermissions.find(p => p === permission);
    }

    public canStartFlow(flowId: string): boolean {
        return this.User.allClaims.filter(c => c.type === LunaClaimTypes.WorkflowInitialize && c.value === flowId).length > 0;
    }

    public hasFlowPermission(id: string): boolean {
        return this.User.allClaims
            .filter(c => c.type == LunaClaimTypes.WorkflowAction && c.value.startsWith(`wfp:${id}`)).length > 0;
    }

    public hasWidgetPermission(widget: Widget): boolean {
        if (!("widgetPermissions" in this.User) || !widget) return false;
        let claimToFind = AuthService.widgetKindClaimLookup[widget.component];
        return this.User.widgetPermissions.includes(claimToFind);
    }

    public checkUserName(username: string, code: string): Observable<boolean> {
        var pp = new HttpParams();
        pp.set("userName", username)
        return this.httpClient.get<any>(`/api/auth/check-user?userName=${encodeURIComponent(username)}&code=${encodeURIComponent(code)}`).pipe(
            map(a => a.available as boolean));
    }

    public redirectToDefaultDashboard(): void {
        const dashboard = this.User.defaultDashboard;

        if (!dashboard) {
            this.router.navigate(["/home"]);
        }
        else {
            this.router.navigate(["dashboard", "view", dashboard]);
        }
    }

    public logout(): void {     
        let identity = new UserIdentity([], "", false);
        this.updateUser(identity);
        sessionStorage.setItem("user", "");
        this.apiToken = "";
        sessionStorage.setItem("userEmail", "");
        sessionStorage.removeItem("selectedOrg");
        sessionStorage.removeItem("user");
        sessionStorage.removeItem("userEmail");
        sessionStorage.removeItem("api_token");
        sessionStorage.removeItem("expDate");
        this.loader.toggleLoader(false);
        this.updateClaimsSubject.next([]);
        this.selectedOrganization.next("");
        setTimeout(() => { window.location.href = "/api/auth/logout"; }, 500);
    }


    public refreshToken(): void {
        let url = "";
        let selectedOrg = "anon";
        if (this.isLoggedIn) {
            selectedOrg = sessionStorage.getItem("selectedOrg");
            url = `/api/auth/refresh-token/`;
        }
        else {
            url = '/api/auth/refresh-token-anon';
        }
        
        this.httpClient.post<LoginTokenResponse>(url, { selectedOrganization: selectedOrg }, { withCredentials: true }).subscribe((response : LoginTokenResponse) => {
            this.sessionRefresh.next(response);
            if (this.isLoggedIn) {
                var authClaim = response.profile.find(x => x.type == "auth");
                var canLogInClaim = response.profile.find(x => x.type == "canLogIn");
                var emailClaim = response.profile.find(x => x.type == "email");
                if (authClaim && authClaim.value == "y" && emailClaim && canLogInClaim && canLogInClaim.value == "y" && response.token) {
                    this.apiToken = response.token;
                    this.tokenExpirationDate = (new Date(response.expDate)).toISOString();
                    return;
                } 
            } else {
                sessionStorage.setItem("anonymous_api_token", response.token);
                this.apiToken = "";
                this.tokenExpirationDate = (new Date(response.expDate)).toISOString();
                return;
            }
        }, failure => {
            this.sessionRefresh.next(null); //pushing null into token response will cause the timeout component to force a logout
            console.error('refresh token failure ', failure);
        });
    }

    public verifyCanLogin(): boolean {
        if (this.isLoggedIn && !this.User.canLogin) {
            return true;
        }
        return false;
    }

    setPermissions(orgId: string, contactId: string, orgCount: number): Observable<Claim[]> {
        return this.httpClient.get<Claim[]>(`/api/auth/setPermissions/${orgId}/${contactId}/${orgCount}`).pipe(tap((claims: Claim[]) => {
            if (sessionStorage.getItem("user")) {
                sessionStorage.removeItem("user");
            }
            let newIdentity = new UserIdentity(claims, this.loggedInUser.getValue().email, true);
            newIdentity.organizationId = orgId;
            this.selectOrganization(orgId);
            this.updateUser(newIdentity);
            sessionStorage.setItem("user", JSON.stringify(claims));
            this.updateClaimsSubject.next(claims);
        }));
    }

    public confirmPassword(email: string, pin: string, password: string): Observable<string> {
        return this.httpClient.post<string>(`/api/auth/confirm-password`, { UserName: email, Code: pin, NewPassword: password });
    }

    public activateAccount(email: string, pin: string, password: string): Observable<string> {
        return this.httpClient.post<string>(`/api/auth/confirm-password`, { UserName: email, Code: pin, NewPassword: password });
    }

    public forgotPassword(email: string): Observable<any> {
        return this.httpClient.post<string>(`/api/auth/forgot-password`, { UserName: email, });
    }

    public changePassword(oldPassword: string, newPassword: string): Observable<any> {
        return this.httpClient.post<string>(`/api/auth/change-password`, { OldPassword: oldPassword, NewPassword: newPassword });
    }

    public updateUser(identity: UserIdentity): void {
        if (!identity.isAnonymous && identity.isLoggedIn && (identity.organizationId == null || identity.organizationId.length === 0)) {
            identity.organizationId = this.selectedOrganization.getValue();
        }
        this.loggedInUser.next(identity);
    }
}
